import { getCountryByValue, getStateOptions } from "./locationData";

/**
 * Format address for provider profile overview (full address)
 * @param {object} provider - Provider object with address fields
 * @returns {string} Full formatted address
 */
export function formatFullAddress(provider) {
  if (!provider) return '';
  
  const parts = [];
  
  // Add primary address (street address)
  if (provider.primaryAddress) {
    parts.push(provider.primaryAddress);
  }
  
  // Add city, state
  if (provider.primaryCityState) {
    parts.push(provider.primaryCityState);
  }
  
  // Add country
  if (provider.primaryCountry) {
    parts.push(provider.primaryCountry);
  }
  
  return parts.join(', ');
}

/**
 * Format address for provider cards and lists (city, state, country only)
 * @param {object} provider - Provider object with address fields
 * @returns {string} City, state, country formatted address
 */
export function formatLocationOnly(provider) {
  if (!provider) return '';
  
  const parts = [];
  const country = provider.primaryCountry || provider.country;
  const cityState = provider.primaryCityState || [provider.city || '', provider.state || ''].filter(s => s).join(', ');
  
  // Add city, state
  if (cityState) {
    parts.push(cityState);
  }
  
  // Add country
  if (country) {
    parts.push(getCountryByValue(country));
  }
  
  return parts.join(', ');
}

/**
 * Format address for vendor cards (compact version showing state/province + country, or city + country)
 * @param {object} provider - Provider object with address fields
 * @returns {string} State/province + country, or city + country if no state
 */
export function formatLocationShort(provider) {
  if (!provider) return '';
  
  const parts = [];
  const country = provider.primaryCountry || provider.country;
  const cityState = provider.primaryCityState || [provider.city || '', provider.state || ''].filter(s => s).join(',');
  
  // Parse city and state from primaryCityState
  if (cityState) {
    const cityStateParts = cityState.split(',').map(part => part.trim());
    
    if (cityStateParts.length > 1) {
      // Has both city and state/province - use state/province
      parts.push(cityStateParts[1]);
    } else if (cityStateParts.length === 1) {
      // Only has city - use city
      parts.push(cityStateParts[0]);
    }
  }
  
  // Add country
  if (country) {
    parts.push(getCountryByValue(country));
  }
  
  return parts.join(', ');
}

/**
 * Get display-ready address for a specific context
 * @param {object} provider - Provider object
 * @param {string} context - 'full' for profile overview, 'location' for cards/lists, 'short' for compact cards
 * @returns {string} Formatted address
 */
export function getProviderAddress(provider, context = 'location') {
  if (context === 'full') return formatFullAddress(provider);
  if (context === 'short') return formatLocationShort(provider);
  return formatLocationOnly(provider);
} 


export const getVQALocationFromProvider = (provider, options = { short: false, abbvState: false }) => {
  if (!provider) return "";
  const {
    provider_address1,
    provider_city,
    provider_state,
    provider_country,
  } = provider;

  return getLocationFromProvider({ address: provider_address1, city: provider_city, state: provider_state, country: provider_country }, options);
};

/**
 * City, state, country for list/grid rows (no street address).
 * Accepts common API shapes: provider_*, location_*, document_* (+ camelCase / PascalCase).
 */
export function getListRowLocationDisplay(
  item,
  options = { short: false, abbvState: false }
) {
  if (!item) return "";

  const city =
    item.provider_city ??
    item.location_city ??
    item.locationCity ??
    item.LocationCity ??
    item.document_city;
  const state =
    item.provider_state ??
    item.location_state ??
    item.locationState ??
    item.LocationState ??
    item.document_state;
  const country =
    item.provider_country ??
    item.location_country ??
    item.locationCountry ??
    item.LocationCountry ??
    item.document_country;

  return getLocationFromProvider(
    { address: "", city, state, country },
    options
  ).trim();
}

export const getLocationFromProvider = (
  provider,
  options = { short: false, abbvState: false }
) => {
  if (!provider) return "";

  const { address, address1, city, state, country } = provider;
  const stateOption = getStateOptions(country).find(
    (option) => option.value === state || option.label === state
  );
  const isUS = ["us", "usa", "united states"]
    .map(val => val.toLowerCase()).includes(country?.toLowerCase() || "");

  const stateValue = state
    ? options.abbvState && isUS
      ? stateOption?.value || ""
      : stateOption?.label || ""
    : "";
  const countryValue = country ? getCountryByValue(country) : "";

  return (
    options.short
      ? [stateValue || city, countryValue]
      : [address || address1, city, stateValue, countryValue]
  ).filter(Boolean).join(", ");
};